import { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Input, Upload, Button, Table, Space, message } from "antd";
import { UploadOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import { BASE_URL } from "../../../utils/config";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
const ALLOWED_EXTENSIONS = ".jpeg, .jpg, .png, .gif, .webp";
const MAX_SIZE_MB = 5;

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [imageError, setImageError] = useState("");
  const [fileList, setFileList] = useState([]);

  const [form, setForm] = useState({
    title: "",
    content: "",
    image: null,
  });

  const fetchBlogs = async () => {
    setTableLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}blogs`);
      setBlogs(res.data.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load blogs. Please try again.");
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Image validation
  const validateImage = (file) => {
    setImageError("");

    if (!ALLOWED_TYPES.includes(file.type)) {
      const msg = `Only ${ALLOWED_EXTENSIONS} images are allowed.`;
      setImageError(msg);
      message.error(msg);
      return false;
    }

    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB > MAX_SIZE_MB) {
      const msg = `Image size must not exceed ${MAX_SIZE_MB}MB.`;
      setImageError(msg);
      message.error(msg);
      return false;
    }

    setForm((prev) => ({ ...prev, image: file }));
    return false; // prevent auto-upload
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      message.warning("Title is required.");
      return;
    }
    if (!form.content.trim()) {
      message.warning("Content is required.");
      return;
    }
    if (!editMode && !form.image) {
      message.warning("Please select an image.");
      return;
    }

    const formData = new FormData();
    formData.append("title", form.title);
    formData.append("content", form.content);
    if (form.image) formData.append("image", form.image);

    setSubmitLoading(true);
    try {
      if (editMode) {
        await axios.post(`${BASE_URL}admin/update/blog/${selectedBlog.id}`, formData);
        message.success("Blog updated successfully!");
      } else {
        await axios.post(`${BASE_URL}admin/create/blog`, formData);
        message.success("Blog created successfully!");
      }

      setOpenModal(false);
      setEditMode(false);
      setForm({ title: "", content: "", image: null });
      setFileList([]);
      setImageError("");
      fetchBlogs();
    } catch (err) {
      console.error(err);
      message.error("Something went wrong. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const confirmDelete = (id) => {
    setDeleteTargetId(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    setDeleteLoading(true);
    try {
      await axios.get(`${BASE_URL}admin/delete/blog/${deleteTargetId}`);
      message.success("Blog deleted successfully!");
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
      fetchBlogs();
    } catch (err) {
      console.error(err);
      message.error("Failed to delete blog. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEdit = (blog) => {
    setEditMode(true);
    setSelectedBlog(blog);
    setForm({ title: blog.title, content: blog.content, image: null });
    setFileList([]);
    setImageError("");
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setEditMode(false);
    setForm({ title: "", content: "", image: null });
    setFileList([]);
    setImageError("");
  };

  const buttonClass = `
    group 
    bg-violet-600 hover:bg-violet-500
    text-white font-medium text-sm
    px-4 rounded-2xl py-1
    shadow-lg shadow-violet-600/30
    transition-all duration-200
    active:scale-[0.98]
  `;

  const deleteButtonClass = `
    group 
    bg-red-600 hover:bg-red-500
    text-white font-medium text-sm
    px-4 rounded-2xl py-1
    shadow-lg shadow-red-600/30
    transition-all duration-200
    active:scale-[0.98]
  `;

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (text) => (
        <img src={text} alt="blog" className="h-16 w-24 object-cover rounded" />
      ),
      width: 100,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => <span className="font-medium">{text}</span>,
      width: 200,
    },
    {
      title: "Content",
      dataIndex: "content",
      key: "content",
      render: (text) => (
        <span className="truncate max-w-xs block" title={text}>
          {text}
        </span>
      ),
      width: 400,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space wrap>
          <button className={buttonClass} onClick={() => handleEdit(record)}>
            Edit
          </button>
          <button className={deleteButtonClass} onClick={() => confirmDelete(record.id)}>
            Delete
          </button>
        </Space>
      ),
      width: 220,
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between mb-4 items-start md:items-center gap-3">
        <h1 className="text-xl font-bold">Blogs</h1>
        <button
          className={buttonClass}
          onClick={() => {
            setOpenModal(true);
            setEditMode(false);
            setForm({ title: "", content: "", image: null });
            setFileList([]);
            setImageError("");
          }}
        >
          Add Blog
        </button>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={blogs.map((b) => ({ ...b, key: b.id }))}
        pagination={{ pageSize: 5 }}
        scroll={{ x: "max-content" }}
        loading={tableLoading}
      />

      {/* Create / Edit Modal */}
      <Modal
        centered
        title={editMode ? "Edit Blog" : "Create Blog"}
        open={openModal}
        onCancel={handleModalClose}
        onOk={handleSubmit}
        okText={
          submitLoading
            ? editMode
              ? "Updating..."
              : "Creating..."
            : editMode
            ? "Update"
            : "Create"
        }
        cancelText="Cancel"
        confirmLoading={submitLoading}
        okButtonProps={{ className: buttonClass, disabled: submitLoading }}
        cancelButtonProps={{
          className: `group bg-gray-500 hover:bg-gray-400 text-white font-medium text-sm px-4 rounded-2xl py-1 shadow-lg shadow-gray-500/30 transition-all duration-200 active:scale-[0.98]`,
          disabled: submitLoading,
        }}
      >
        <div className="space-y-3">
          <div>
            <Input
              placeholder="Title *"
              name="title"
              value={form.title}
              onChange={handleChange}
              disabled={submitLoading}
            />
          </div>

          <div>
            <Input.TextArea
              placeholder="Content *"
              name="content"
              value={form.content}
              onChange={handleChange}
              rows={10}
              disabled={submitLoading}
            />
          </div>

          <div>
            <Upload
              beforeUpload={validateImage}
              fileList={fileList}
              onChange={({ fileList: newFileList }) => {
                // Only keep last selected file
                setFileList(newFileList.slice(-1));
              }}
              onRemove={() => {
                setForm((prev) => ({ ...prev, image: null }));
                setFileList([]);
                setImageError("");
              }}
              maxCount={1}
              accept={ALLOWED_TYPES.join(",")}
              disabled={submitLoading}
            >
              <Button icon={<UploadOutlined />} disabled={submitLoading}>
                Upload Image
              </Button>
            </Upload>

            <p className="text-xs text-gray-400 mt-1">
              Allowed: {ALLOWED_EXTENSIONS} | Max size: {MAX_SIZE_MB}MB
            </p>

            {imageError && (
              <p className="text-xs text-red-500 mt-1 font-medium">{imageError}</p>
            )}

            {editMode && !form.image && (
              <p className="text-xs text-gray-400 mt-1">
                * Existing image will be kept if no new image is uploaded
              </p>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        centered
        open={deleteConfirmOpen}
        onCancel={() => {
          if (!deleteLoading) {
            setDeleteConfirmOpen(false);
            setDeleteTargetId(null);
          }
        }}
        onOk={handleDeleteConfirmed}
        okText={deleteLoading ? "Deleting..." : "Yes, Delete"}
        cancelText="No, Cancel"
        confirmLoading={deleteLoading}
        okButtonProps={{ className: deleteButtonClass, disabled: deleteLoading }}
        cancelButtonProps={{
          className: `group bg-gray-500 hover:bg-gray-400 text-white font-medium text-sm px-4 rounded-2xl py-1 shadow-lg shadow-gray-500/30 transition-all duration-200 active:scale-[0.98]`,
          disabled: deleteLoading,
        }}
        closable={!deleteLoading}
      >
        <div className="flex items-start gap-3 py-2">
          <ExclamationCircleFilled className="text-red-500 text-xl mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-gray-800 text-base">
              Are you sure?
            </p>
            <p className="text-gray-500 text-sm mt-1">
              This blog will be permanently deleted. This action cannot be undone.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Blogs;